import {Connection, Location, Meter, Organisation} from "@flowmaps-com/flowmaps-typescriptmodels";
import {RefdataUtils} from "../views/refdata/refdata-utils";
import {
    effectiveMeterChildIds,
    effectiveMeterParentId,
    effectiveMeterRootIds,
    isMeterVisible
} from '../views/refdata/meters/submeter-role.utils';

export class Entity {
    organisation: Organisation;
    location?: Location;
    connection?: Connection;
    meter?: Meter;
    searchAlias: string[];

    constructor(organisation: Organisation, location?: Location, connection?: Connection, meter?: Meter) {
        this.organisation = organisation;
        this.location = location;
        this.connection = connection;
        this.meter = meter;
        this.searchAlias = this.getSearchAlias();
    }

    getEntityType(): EntityType {
        return this.meter ? EntityType.meter
            : this.connection ? EntityType.connection
                : this.location ? EntityType.location
                    : EntityType.organisation;
    }

    getEntityId(): string {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return this.organisation.organisationId;
            case EntityType.location:
                return this.location.locationId;
            case EntityType.connection:
                return this.connection.connectionId;
            case EntityType.meter:
                return this.meter.meterId;
        }
    }

    getFormattedName(): string {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return RefdataUtils.organisationFormatter(this.organisation);
            case EntityType.location:
                return RefdataUtils.locationInfoFormatter(this.location.info);
            case EntityType.connection:
                return RefdataUtils.connectionFormatter(this.connection);
            case EntityType.meter:
                return RefdataUtils.meterFormatter(this);
        }
    }

    getParentAsEntity(): Entity {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return null;
            case EntityType.location:
                return new Entity(this.organisation);
            case EntityType.connection:
                return new Entity(this.organisation, this.location);
            case EntityType.meter:
                const parentId = effectiveMeterParentId(this.meter, this.connection.meters);
                return parentId != null
                    ? new Entity(this.organisation, this.location, this.connection, this.connection.meters.find(m => m.meterId === parentId))
                    : new Entity(this.organisation, this.location, this.connection);
        }
    }

    getChildIds(): string[] {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return this.organisation.locations.map(l => l.locationId);
            case EntityType.location:
                return this.location.connections.map(c => c.connectionId);
            case EntityType.connection:
                return effectiveMeterRootIds(this.connection.meters);
            case EntityType.meter:
                return effectiveMeterChildIds(this.meter, this.connection.meters);
        }
    }

    getChildEntities(): Entity[] {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return this.organisation.locations.map(l => new Entity(this.organisation, l));
            case EntityType.location:
                return this.location.connections.map(c => new Entity(this.organisation, this.location, c));
            case EntityType.connection:
                const connectionRootIds = effectiveMeterRootIds(this.connection.meters);
                return this.connection.meters.filter(m => isMeterVisible(m)
                        && (connectionRootIds.includes(m.meterId) || this.connection.info.connectionType == "ChargePoint"))
                    .map(m => new Entity(this.organisation, this.location, this.connection, m));
            case EntityType.meter:
                const childIds = effectiveMeterChildIds(this.meter, this.connection.meters);
                return this.connection.meters.filter(m => childIds.includes(m.meterId))
                    .map(m => new Entity(this.organisation, this.location, this.connection, m));
        }
    }

    getMeters(): Entity[] {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return this.organisation.locations.flatMap(l => l.connections.flatMap(c => {
                    const rootIds = effectiveMeterRootIds(c.meters);
                    return c.meters.filter(m => isMeterVisible(m) && rootIds.includes(m.meterId))
                        .map(m => new Entity(this.organisation, l, c, m));
                }));
            case EntityType.location:
                return this.location.connections.flatMap(c => {
                    const rootIds = effectiveMeterRootIds(c.meters);
                    return c.meters.filter(m => isMeterVisible(m)
                            && (rootIds.includes(m.meterId) || c.info.connectionType == "ChargePoint"))
                        .map(m => new Entity(this.organisation, this.location, c, m));
                });
            case EntityType.connection:
                const meterRootIds = effectiveMeterRootIds(this.connection.meters);
                return this.connection.meters.filter(m => isMeterVisible(m)
                        && (meterRootIds.includes(m.meterId) || this.connection.info.connectionType == "ChargePoint"))
                    .map(m => new Entity(this.organisation, this.location, this.connection, m));
            case EntityType.meter:
                const childIds = effectiveMeterChildIds(this.meter, this.connection.meters);
                return this.connection.meters.filter(m => childIds.includes(m.meterId))
                    .map(m => new Entity(this.organisation, this.location, this.connection, m));
        }
    }

    private getSearchAlias(): string[] {
        switch (this.getEntityType()) {
            case EntityType.organisation:
                return []
            case EntityType.location:
                const address = this.location.info?.address
                    ? RefdataUtils.addressFormatter(this.location.info.address, true) : null;
                const connectionCodes = (this.location.connections ?? []).map(c => c.info?.code).filter(c => c);
                return (address ? [address, address.replace(",", "")] : []).concat(connectionCodes);
            case EntityType.connection:
                return []
            case EntityType.meter:
                return [];
        }
    }

    static getExcludedFilterFields(excludedFields: string[] = []): string[] {
        return ["locations", "connections", "meters", "contracts", "timeZone",
            "givenAuthorisations", "receivedAuthorisations"].concat(excludedFields);
    }
}

export enum EntityType {
    organisation = "organisation",
    location = "location",
    connection = "connection",
    meter = "meter"
}
